import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';
import { buildApiUrl } from '../utils/apiConfig';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 16, 24, 0.45);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => (theme.zIndex?.modal || 2100)};
`;

const Modal = styled.div`
  width: min(1120px, calc(100vw - 24px));
  max-height: min(780px, calc(100vh - 24px));
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 18px;
  box-shadow: 0 26px 80px rgba(15, 23, 42, 0.32);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  background: #ffffff;
`;

const HeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderButton = styled.button`
  border: 1px solid rgba(15, 23, 42, 0.2);
  background: #ffffff;
  color: ${({ theme }) => theme.colors.neutral[800]};
  border-radius: 10px;
  padding: 7px 10px;
  font-size: 12px;
  cursor: pointer;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);
  min-height: 0;
  flex: 1;
`;

const Sidebar = styled.div`
  border-right: 1px solid rgba(15, 23, 42, 0.08);
  background: #ffffff;
  padding: 12px;
  overflow: auto;
`;

const TierButton = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid ${({ $active }) => ($active ? '#0f172a' : 'rgba(15, 23, 42, 0.14)')};
  background: ${({ $active }) => ($active ? '#e2e8f0' : '#fff')};
  border-radius: 12px;
  padding: 10px;
  cursor: pointer;
  margin-bottom: 8px;
`;

const TierName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const TierMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.neutral[600]};
`;

const Main = styled.div`
  overflow: auto;
  padding: 12px;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid rgba(15, 23, 42, 0.2);
  border-radius: 10px;
  padding: 10px;
  font-size: 13px;
  margin-bottom: 12px;
  background: #fff;
`;

const ServiceBlock = styled.div`
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  padding: 12px;
  margin-bottom: 12px;
`;

const ServiceTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral[900]};
  margin-bottom: 4px;
`;

const ServiceSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral[600]};
  margin-bottom: 10px;
`;

const CapabilityRow = styled.div`
  border-top: 1px solid rgba(15, 23, 42, 0.07);
  padding: 10px 0;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
`;

const CapabilityInfo = styled.div`
  min-width: 0;
`;

const CapabilityCode = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const CapabilityDesc = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral[600]};
`;

const EffectButtons = styled.div`
  display: inline-flex;
  gap: 6px;
`;

const EffectButton = styled.button`
  border: 1px solid rgba(15, 23, 42, 0.2);
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 11px;
  cursor: pointer;
  background: ${({ $active, $variant }) => {
    if (!$active) return '#fff';
    if ($variant === 'allow') return '#dcfce7';
    if ($variant === 'deny') return '#fee2e2';
    return '#e2e8f0';
  }};
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const Empty = styled.div`
  padding: 16px;
  border: 1px dashed rgba(15, 23, 42, 0.2);
  border-radius: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.neutral[600]};
  background: #fff;
`;

const ErrorText = styled.div`
  color: #b91c1c;
  font-size: 12px;
`;

function normalizeEffect(effect) {
  if (effect === 'allow' || effect === 'deny') return effect;
  return 'inherit';
}

export default function TierCapabilityEditorModal({ open, onClose, getApiHeaders }) {
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [tiers, setTiers] = useState([]);
  const [services, setServices] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchMatrix = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError('');
    try {
      const headers = await getApiHeaders();
      const response = await axios({
        url: buildApiUrl('auth/staff/capability-matrix/'),
        method: 'get',
        headers,
      });
      const data = response.data || {};
      const nextTiers = Array.isArray(data.tiers) ? data.tiers : [];
      const nextServices = Array.isArray(data.services) ? data.services : [];
      const nextMatrix = data.matrix && typeof data.matrix === 'object' ? data.matrix : {};
      setTiers(nextTiers);
      setServices(nextServices);
      setMatrix(nextMatrix);
      setSelectedTierId((prev) => {
        if (prev && nextTiers.some((tier) => Number(tier.id) === Number(prev))) {
          return prev;
        }
        return nextTiers[0]?.id ?? null;
      });
    } catch (err) {
      console.error('Failed to fetch capability matrix:', err);
      const detail = err?.response?.data?.detail;
      setError(detail || 'Could not load capability matrix.');
      setTiers([]);
      setServices([]);
      setMatrix({});
      setSelectedTierId(null);
    } finally {
      setLoading(false);
    }
  }, [getApiHeaders, open]);

  useEffect(() => {
    if (open) {
      fetchMatrix();
    }
  }, [open, fetchMatrix]);

  const filteredServices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return services;

    return services
      .map((service) => {
        const capabilities = (service.capabilities || []).filter((capability) => {
          const haystack = [
            capability.code,
            capability.description,
            capability.action,
            capability.scope,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(query);
        });

        const serviceMatch = [service.name, service.key, service.description]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query);

        if (serviceMatch) {
          return service;
        }
        return {
          ...service,
          capabilities,
        };
      })
      .filter((service) => (service.capabilities || []).length > 0);
  }, [searchQuery, services]);

  const selectedTierKey = selectedTierId != null ? String(selectedTierId) : '';

  const getCurrentEffect = useCallback((capabilityCode) => {
    const tierRow = matrix[selectedTierKey] || {};
    return normalizeEffect(tierRow[capabilityCode]);
  }, [matrix, selectedTierKey]);

  const updateEffect = useCallback(async (capabilityCode, nextEffect) => {
    if (!selectedTierKey) return;

    const effect = normalizeEffect(nextEffect);
    const previous = matrix[selectedTierKey]?.[capabilityCode] || 'inherit';
    if (normalizeEffect(previous) === effect) {
      return;
    }

    const rowKey = `${selectedTierKey}:${capabilityCode}`;
    setSavingKey(rowKey);
    setError('');

    setMatrix((prev) => {
      const next = { ...prev };
      const tierRow = { ...(next[selectedTierKey] || {}) };
      if (effect === 'inherit') {
        delete tierRow[capabilityCode];
      } else {
        tierRow[capabilityCode] = effect;
      }
      next[selectedTierKey] = tierRow;
      return next;
    });

    try {
      const headers = await getApiHeaders();
      const response = await axios({
        url: buildApiUrl('auth/staff/capability-matrix/'),
        method: 'patch',
        headers,
        data: {
          tier_id: Number(selectedTierKey),
          capability_code: capabilityCode,
          effect,
        },
      });

      const resolved = normalizeEffect(response.data?.effect);
      setMatrix((prev) => {
        const next = { ...prev };
        const tierRow = { ...(next[selectedTierKey] || {}) };
        if (resolved === 'inherit') {
          delete tierRow[capabilityCode];
        } else {
          tierRow[capabilityCode] = resolved;
        }
        next[selectedTierKey] = tierRow;
        return next;
      });
    } catch (err) {
      console.error('Failed to update capability effect:', err);
      const detail = err?.response?.data?.detail;
      setError(detail || 'Could not update capability.');

      setMatrix((prev) => {
        const next = { ...prev };
        const tierRow = { ...(next[selectedTierKey] || {}) };
        const normalizedPrevious = normalizeEffect(previous);
        if (normalizedPrevious === 'inherit') {
          delete tierRow[capabilityCode];
        } else {
          tierRow[capabilityCode] = normalizedPrevious;
        }
        next[selectedTierKey] = tierRow;
        return next;
      });
    } finally {
      setSavingKey('');
    }
  }, [getApiHeaders, matrix, selectedTierKey]);

  if (!open) return null;

  return (
    <Overlay data-scroll-overlay="true" onClick={onClose} role="presentation">
      <Modal data-scroll-overlay="true" onClick={(event) => event.stopPropagation()}>
        <Header>
          <HeaderTitle>Tier Capability Editor</HeaderTitle>
          <HeaderActions>
            <HeaderButton type="button" onClick={fetchMatrix}>Refresh</HeaderButton>
            <HeaderButton type="button" onClick={onClose} aria-label="Close tier capability editor">
              <Icon name="close" />
            </HeaderButton>
          </HeaderActions>
        </Header>

        <Body>
          <Sidebar>
            {tiers.map((tier) => {
              const active = Number(selectedTierId) === Number(tier.id);
              return (
                <TierButton
                  key={tier.id}
                  type="button"
                  $active={active}
                  onClick={() => setSelectedTierId(tier.id)}
                >
                  <TierName>{tier.name}</TierName>
                  <TierMeta>{tier.billing_period || 'n/a'}</TierMeta>
                </TierButton>
              );
            })}
          </Sidebar>

          <Main>
            <SearchInput
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Filter capabilities by code, action, scope, or description"
              aria-label="Search capabilities"
            />

            {loading && <Empty>Loading capability matrix...</Empty>}
            {!loading && error && <ErrorText>{error}</ErrorText>}

            {!loading && !selectedTierId && <Empty>No subscription tiers available.</Empty>}

            {!loading && selectedTierId && filteredServices.length === 0 && (
              <Empty>No matching capabilities for this filter.</Empty>
            )}

            {!loading && selectedTierId && filteredServices.map((service) => (
              <ServiceBlock key={service.id || service.key}>
                <ServiceTitle>{service.name || service.key}</ServiceTitle>
                <ServiceSub>{service.description || service.key}</ServiceSub>

                {(service.capabilities || []).map((capability) => {
                  const currentEffect = getCurrentEffect(capability.code);
                  const rowKey = `${selectedTierKey}:${capability.code}`;
                  const isSaving = savingKey === rowKey;

                  return (
                    <CapabilityRow key={capability.code}>
                      <CapabilityInfo>
                        <CapabilityCode>{capability.code}</CapabilityCode>
                        <CapabilityDesc>
                          {capability.description || `${capability.action} (${capability.scope})`}
                        </CapabilityDesc>
                      </CapabilityInfo>
                      <EffectButtons>
                        <EffectButton
                          type="button"
                          $variant="inherit"
                          $active={currentEffect === 'inherit'}
                          onClick={() => updateEffect(capability.code, 'inherit')}
                          disabled={isSaving}
                        >
                          Inherit
                        </EffectButton>
                        <EffectButton
                          type="button"
                          $variant="allow"
                          $active={currentEffect === 'allow'}
                          onClick={() => updateEffect(capability.code, 'allow')}
                          disabled={isSaving}
                        >
                          Allow
                        </EffectButton>
                        <EffectButton
                          type="button"
                          $variant="deny"
                          $active={currentEffect === 'deny'}
                          onClick={() => updateEffect(capability.code, 'deny')}
                          disabled={isSaving}
                        >
                          Deny
                        </EffectButton>
                      </EffectButtons>
                    </CapabilityRow>
                  );
                })}
              </ServiceBlock>
            ))}
          </Main>
        </Body>
      </Modal>
    </Overlay>
  );
}
